import { yupResolver } from "@hookform/resolvers/yup";
import EditIcon from "@mui/icons-material/Edit";
import { Attachment } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { FileGroup } from "common/FileUpload";
import { enqueueSnackbar } from "notistack";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import { useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  createSupplier,
  searchSuppliers,
  getTransTypes,
  updateSupplier,
} from "src/apis/controllers/expenses";
import { getFiles } from "src/apis/controllers/files";
import { FileProvider, useFileContext } from "src/context/FilesV2";
import CurrencyInput from "src/qubCommon/Inputs/CurrencyInput";
import {
  CssTextField,
  DateInput,
  Dropdown,
  TextInput,
} from "src/qubCommon/Inputs";
import {
  deleteBundle,
  deleteFile,
  downloadFile,
  formatFiles,
  uploadFiles,
} from "src/util";
import {
  getResponseMessage,
  getResponsePayload,
  isErrorResponse,
} from "./headerPayload";
import SupplierDialog from "./SupplierDialog";
import * as yup from 'yup'

const PAYMENT_ATTACHMENTS_PROJECT_TABLE_ID = 18;

const normalizeMode = (mode) => mode?.toString?.().trim().toLowerCase() ?? "";

const isRequiredMode = (mode) => normalizeMode(mode) === "required";

const isHiddenMode = (mode) => normalizeMode(mode) === "hidden";

const isTrueFlag = (value) =>
  value === true || value?.toString?.().trim?.().toLowerCase?.() === "true";

const normalizeAfm = (value) => value?.replace?.(/\D/g, "").slice(0, 9) ?? "";

const CREATE_SUPPLIER_OPTION_ID = "__create_supplier__";

const normalizeSupplierSearch = (value) => value?.toString?.().trim?.() ?? "";

const isCreateSupplierOption = (option) =>
  option?.id === CREATE_SUPPLIER_OPTION_ID || option?.isCreateOption === true;

const makeCreateSupplierOption = (searchText) => ({
  id: CREATE_SUPPLIER_OPTION_ID,
  isCreateOption: true,
  searchText,
});

const getSupplierOptionLabel = (option) => {
  if (!option) return "";
  if (typeof option === "string") return option;
  if (isCreateSupplierOption(option)) {
    return option.searchText
      ? `Δημιουργία νέου προμηθευτή: ${option.searchText}`
      : "Δημιουργία νέου προμηθευτή";
  }

  const title = option.onomasia || option.commerTitle || "Προμηθευτής";
  return [title, option.afm].filter(Boolean).join(" - ");
};

const emptyToNull = (value) => {
  const nextValue = value?.toString?.().trim?.() ?? "";
  return nextValue === "" ? null : nextValue;
};

const hasSelectedValue = (value) =>
  value !== undefined && value !== null && value?.toString?.().trim?.() !== "";

const normalizeDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString().slice(0, 10);

  const nextValue = value.toString();
  return nextValue.includes("T") ? nextValue.split("T")[0] : nextValue;
};

const parseAmount = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  let normalized = value
    .toString()
    .replace(/\s/g, "")
    .replace("€", "")
    .trim();

  if (normalized.includes(",") && normalized.includes(".")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else {
    normalized = normalized.replace(",", ".");
  }

  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
};

const amountOrNull = (value) => {
  const amount = parseAmount(value);
  return amount === null ? null : amount;
};

const amountFormatter = new Intl.NumberFormat("el-GR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatAmount = (value) => amountFormatter.format(Number(value ?? 0));

const formatAmountForInput = (value) => {
  const amount = parseAmount(value);
  return amount === null ? "" : formatAmount(amount);
};

const formatPaymentInitialAmount = (value) => {
  const amount = parseAmount(value);
  return amount === null || amount <= 0 ? "" : formatAmount(amount);
};

const amountFieldSchema = yup.mixed().test(
  "payment-amount-positive",
  "Το ποσό πρέπει να είναι μεγαλύτερο από 0.",
  (value) => {
    const amount = parseAmount(value);
    return amount === null || amount > 0;
  }
);

const cashAmountFieldSchema = amountFieldSchema.test(
  "cash-amount-limit",
  "Το ποσό μετρητών πρέπει να είναι μικρότερο από 500,00 €.",
  (value) => {
    const amount = parseAmount(value);
    return amount === null || amount < 500;
  }
);

const paymentSchema = yup
  .object({
    paymentTypeId: yup
      .mixed()
      .test(
        "payment-type-required",
        "Απαιτείται επιλογή είδους πληρωμής.",
        (value) => value !== undefined && value !== null && value !== ""
      ),
    paymentTypeSupplierMode: yup.string().nullable(),
    paymentDate: yup
      .mixed()
      .test("payment-date-required", "Απαιτείται ημερομηνία.", (value) => !!value),
    paymentNumber: yup
      .string()
      .trim()
      .required("Απαιτείται αριθμός πληρωμής."),
    description: yup.string().nullable(),
    supplierAfm: yup.string().nullable(),
    supplierId: yup
      .mixed()
      .nullable()
      .test(
        "supplier-selected",
        "Αναζητήστε ή δημιουργήστε προμηθευτή.",
        (value, context) => {
          const mode = context?.parent?.paymentTypeSupplierMode;

          if (isHiddenMode(mode)) return true;
          if (isRequiredMode(mode)) return !!value;
          return true;
        }
      ),
    cashAmount: cashAmountFieldSchema,
    cardAmount: amountFieldSchema,
    bankAmount: amountFieldSchema,
    comments: yup.string().nullable(),
    remarks: yup.string().nullable(),
  })
  .test(
    "at-least-one-payment-amount",
    "Συμπληρώστε τουλάχιστον ένα ποσό πληρωμής.",
    (values, context) => {
      const hasAmount = ["cashAmount", "cardAmount", "bankAmount"].some(
        (fieldName) => parseAmount(values?.[fieldName]) > 0
      );

      if (hasAmount) return true;

      return context.createError({
        path: "cashAmount",
        message: "Συμπληρώστε τουλάχιστον ένα ποσό πληρωμής.",
      });
    }
  );

const initialFormValues = {
  paymentTypeId: "",
  paymentTypeName: "",
  paymentTypeSupplierMode: "",
  paymentDate: "",
  paymentNumber: "",
  description: "",
  supplierId: null,
  supplierAfm: "",
  supplierSelectedAfm: "",
  supplierName: "",
  supplierCanEdit: false,
  supplierCommercialTitle: "",
  supplierDoy: "",
  supplierDoyDescr: "",
  street: "",
  streetNumber: "",
  postalCode: "",
  city: "",
  cashAmount: "",
  cardAmount: "",
  bankAmount: "",
  comments: "",
  remarks: "",
};

const getInitialValues = (payment) => ({
  ...initialFormValues,
  paymentTypeId: payment?.transTypeId ?? "",
  paymentTypeName: payment?.transTypeName ?? "",
  paymentDate: payment?.transDate ?? payment?.paymentDate ?? "",
  paymentNumber: payment?.transNumber ?? payment?.paymentNumber ?? "",
  description: payment?.description ?? "",
  supplierId: payment?.supplierId ?? null,
  supplierAfm: payment?.supplierAfm ?? "",
  supplierSelectedAfm: payment?.supplierAfm ?? "",
  supplierName: payment?.supplierName ?? "",
  supplierCanEdit: payment?.supplierCanEdit ?? false,
  street: payment?.street ?? payment?.postalAddress ?? "",
  streetNumber: payment?.streetNumber ?? payment?.postalAddressNo ?? "",
  postalCode: payment?.postalCode ?? payment?.postalZipCode ?? "",
  city: payment?.city ?? payment?.postalAreaDescription ?? "",
  cashAmount: formatPaymentInitialAmount(payment?.cashAmount),
  cardAmount: formatPaymentInitialAmount(payment?.cardAmount),
  bankAmount: formatPaymentInitialAmount(payment?.bankAmount),
  comments: payment?.comments ?? "",
  remarks: payment?.remarks ?? "",
});

const getTransTypeOptions = (res) => {
  const payload = getResponsePayload(res);

  if (!Array.isArray(payload)) return [];

  return payload.map((transType) => ({
    ...transType,
    value: transType.id,
    label: transType.descr ?? transType.abbr ?? "",
  }));
};

const getAttachmentRequest = (paymentTransHeaderId) => ({
  DataKey: "MHD_TransHeader_Expenses",
  DataValue: null,
  RecordId: null,
  RecordGuid: paymentTransHeaderId ?? null,
  ProjectTableId: PAYMENT_ATTACHMENTS_PROJECT_TABLE_ID,
});

const getBundleIds = (fileGroups, fallbackBundleIds = []) => {
  const bundleIds = fileGroups.reduce((acc, group) => {
    const groupBundleIds =
      group.bundles
        ?.filter((bundle) => bundle?.files?.length > 0)
        .map((bundle) => bundle.id) ?? [];

    return [...acc, ...groupBundleIds];
  }, []);

  return bundleIds.length > 0 ? bundleIds : fallbackBundleIds ?? [];
};

const mapSupplierToFormValues = (supplier) => ({
  supplierId: supplier?.id ?? null,
  supplierAfm: supplier?.afm ?? "",
  supplierSelectedAfm: supplier?.afm ?? "",
  supplierName: supplier?.onomasia ?? supplier?.commerTitle ?? "",
  supplierCanEdit: supplier?.canEdit === true,
  supplierCommercialTitle: supplier?.commerTitle ?? "",
  supplierDoy: supplier?.doy ?? "",
  supplierDoyDescr: supplier?.doyDescr ?? "",
  street: supplier?.postalAddress ?? "",
  streetNumber: supplier?.postalAddressNo ?? "",
  postalCode: supplier?.postalZipCode ?? "",
  city: supplier?.postalAreaDescription ?? "",
});

const inputSx = {
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
};

const formSurfaceSx = {
  width: "100%",
  maxWidth: 760,
  mx: "auto",
};

const paymentFormSurfaceSx = {
  ...formSurfaceSx,
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "max-content minmax(0, 1fr)" },
  columnGap: 1,
  rowGap: 0.75,
  alignItems: "center",
};

const paymentFormRowSx = {
  display: { xs: "grid", sm: "contents" },
  gridTemplateColumns: "1fr",
  mb: { xs: 0.75, sm: 0 },
};

const paymentLabelSx = {
  px: 1,
  color: "#111",
  fontSize: 18,
  fontStyle: "italic",
  lineHeight: 1.1,
  textAlign: { xs: "left", sm: "right" },
  whiteSpace: { sm: "nowrap" },
};

const paymentFieldSx = {
  minWidth: 0,
  px: 1,
  py: 0.35,
  "& > *": {
    width: "100%",
  },
};

const paymentAmountsGridSx = {
  ...formSurfaceSx,
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md: "repeat(3, minmax(0, 1fr)) max-content",
  },
  alignItems: "end",
  columnGap: 2,
  rowGap: 1.25,
};

const paymentAmountFieldSx = {
  minWidth: 0,
  "& .MuiFormControl-root": {
    width: "100%",
  },
};

const paymentAmountTotalSx = {
  borderLeft: { md: "1px solid #d6dbe6" },
  minWidth: { xs: "auto", md: 130 },
  pl: { xs: 0, md: 2 },
  pt: { xs: 0.25, md: 0 },
  textAlign: { xs: "right", md: "left" },
};

const PaymentFormRow = ({ label, children, minHeight = 42 }) => (
  <Box sx={paymentFormRowSx}>
    <Box
      sx={{
        ...paymentLabelSx,
        alignSelf: "center",
        minHeight,
      }}
    >
      {label}:
    </Box>
    <Box
      sx={{
        ...paymentFieldSx,
        alignSelf: "center",
        minHeight,
      }}
    >
      {children}
    </Box>
  </Box>
);

const PaymentAmountCell = ({ label, children }) => (
  <Box sx={paymentAmountFieldSx}>
    <Typography
      sx={{
        color: "#111",
        fontSize: 14,
        fontWeight: 800,
        mb: 0.25,
        textAlign: "left",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const PaymentAmountTotal = ({ total }) => (
  <Box sx={paymentAmountTotalSx}>
    <Typography
      sx={{
        color: "text.secondary",
        fontSize: 13,
        fontWeight: 800,
        lineHeight: 1.1,
      }}
    >
      Σύνολο
    </Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 900, lineHeight: 1.25 }}>
      {formatAmount(total)} €
    </Typography>
  </Box>
);

const PaymentAttachments = ({ settings, isSaving = false }) => {
  const { fileGroups } = useFileContext();
  const canAdd = settings?.canAdd === true;
  const canEdit = settings?.canEdit === true;
  const canDelete = settings?.canDelete === true;
  const isReadOnly = !(canAdd || canEdit);
  const hasFileGroups = fileGroups?.length > 0;

  if (!hasFileGroups) return null;

  const AccordionTitle = () => (
    <Stack direction="row" gap={1} alignItems="center">
      <Attachment color="primary" fontSize="small" sx={{ ml: "2px" }} />
      <Typography color="primary" sx={{ fontWeight: 1000 }}>
        ΣΥΝΗΜΜΕΝΑ ΠΛΗΡΩΜΗΣ
      </Typography>
    </Stack>
  );

  const AccordionContent = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {fileGroups.map((group) => (
          <FileGroup
            reviewMode={false}
            isReadOnly={isReadOnly || isSaving}
            canAdd={canAdd}
            canDelete={canDelete}
            key={group.id}
            id={group.id}
            title={group?.title}
            maxSize={2097152}
            maxFiles={5}
            maxBundles={group?.maxBundles}
            fields={group.fields}
            bundles={group?.bundles}
            templates={group?.templates}
          />
        ))}
      </Grid>
    </Grid>
  );

  return (
    <CustomSimpleAccordion
      title={<AccordionTitle />}
      content={AccordionContent}
    />
  );
};

const PaymentDialogContent = ({
  open,
  mode = "create",
  expTransHeaderId,
  initialValues,
  attachmentSettings,
  paidOnIssue,
  isSaving = false,
  onCancel,
  onSave,
}) => {
  const isEditMode = mode === "edit";
  const paymentTransHeaderId = initialValues?.paymentTransHeaderId;
  const form = useForm({
    resolver: yupResolver(paymentSchema),
    defaultValues: initialFormValues,
  });
  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
    setValue,
    trigger,
    watch,
  } = form;
  const { control: filesControl, fileGroups } = useFileContext();
  const [transTypeOptions, setTransTypeOptions] = useState([]);
  const [metadataError, setMetadataError] = useState(null);
  const [isMetadataLoading, setIsMetadataLoading] = useState(false);
  const [hasLoadedTransTypes, setHasLoadedTransTypes] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false);
  const [isSupplierLookupLoading, setIsSupplierLookupLoading] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [searchedSupplierText, setSearchedSupplierText] = useState("");
  const [hasSupplierSearchError, setHasSupplierSearchError] = useState(false);
  const [isSupplierSaving, setIsSupplierSaving] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [supplierDialogState, setSupplierDialogState] = useState({
    open: false,
    supplier: null,
    initialAfm: "",
  });

  const paymentTypeId = watch("paymentTypeId");
  const paymentTypeSupplierMode = watch("paymentTypeSupplierMode");
  const paymentDate = watch("paymentDate");
  const supplierId = watch("supplierId");
  const supplierAfm = watch("supplierAfm");
  const supplierName = watch("supplierName");
  const supplierCanEdit = watch("supplierCanEdit");
  const supplierCommercialTitle = watch("supplierCommercialTitle");
  const supplierDoy = watch("supplierDoy");
  const supplierDoyDescr = watch("supplierDoyDescr");
  const cashAmount = watch("cashAmount");
  const cardAmount = watch("cardAmount");
  const bankAmount = watch("bankAmount");

  const selectedTransType = useMemo(
    () =>
      transTypeOptions.find(
        (option) => option.value?.toString() === paymentTypeId?.toString()
      ),
    [paymentTypeId, transTypeOptions]
  );

  const hasPaymentType =
    paymentTypeId !== undefined && paymentTypeId !== null && paymentTypeId !== "";
  const supplierMode =
    selectedTransType?.supplierMode ?? paymentTypeSupplierMode ?? "";
  const isSupplierVisible =
    hasPaymentType && !!supplierMode && !isHiddenMode(supplierMode);
  const isSupplierRequired = isRequiredMode(supplierMode);
  const canEditSupplier =
    isSupplierVisible && !!supplierId && supplierCanEdit === true;
  const selectedSupplier = useMemo(
    () =>
      supplierId
        ? {
          id: supplierId,
          afm: supplierAfm,
          onomasia: supplierName,
          commerTitle: supplierCommercialTitle,
          doy: supplierDoy,
          doyDescr: supplierDoyDescr,
          canEdit: supplierCanEdit,
        }
        : null,
    [
      supplierAfm,
      supplierCanEdit,
      supplierCommercialTitle,
      supplierDoy,
      supplierDoyDescr,
      supplierId,
      supplierName,
    ]
  );
  const supplierAutocompleteOptions = useMemo(() => {
    const options = Array.isArray(supplierOptions) ? [...supplierOptions] : [];

    if (
      selectedSupplier?.id &&
      !options.some(
        (option) => option.id?.toString() === selectedSupplier.id?.toString()
      )
    ) {
      options.unshift(selectedSupplier);
    }

    const currentSearch = normalizeSupplierSearch(supplierSearch);
    const shouldShowCreateOption =
      currentSearch &&
      currentSearch === searchedSupplierText &&
      !isSupplierLookupLoading &&
      !hasSupplierSearchError &&
      !supplierOptions.length;

    if (shouldShowCreateOption) {
      options.push(makeCreateSupplierOption(currentSearch));
    }

    return options;
  }, [
    hasSupplierSearchError,
    isSupplierLookupLoading,
    searchedSupplierText,
    selectedSupplier,
    supplierOptions,
    supplierSearch,
  ]);
  const isDialogReady = isFormInitialized && hasLoadedTransTypes;
  const canSave =
    isDialogReady && !isSaving && !isUploadingFiles && !isMetadataLoading;
  const paymentAmountTotal = useMemo(
    () =>
      [cashAmount, cardAmount, bankAmount].reduce(
        (sum, value) => sum + (parseAmount(value) ?? 0),
        0
      ),
    [bankAmount, cardAmount, cashAmount]
  );
  const hideAttachments = isTrueFlag(paidOnIssue ?? initialValues?.paidOnIssue);

  const handleAmountBlur = (fieldName) => (event) => {
    setValue(fieldName, formatAmountForInput(event.target.value), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (!open) {
      setIsFormInitialized(false);
      return;
    }

    reset(getInitialValues(initialValues));
    setSupplierDialogState({ open: false, supplier: null, initialAfm: "" });
    setSupplierOptions([]);
    setSupplierSearch("");
    setSearchedSupplierText("");
    setHasSupplierSearchError(false);
    setMetadataError(null);
    setHasLoadedTransTypes(false);
    setIsFormInitialized(true);
  }, [initialValues, open, reset]);

  useEffect(() => {
    if (!open) return undefined;

    let isActive = true;

    const fetchTransTypes = async () => {
      setIsMetadataLoading(true);
      setHasLoadedTransTypes(false);
      setMetadataError(null);

      try {
        const res = await getTransTypes(true);
        if (!isActive) return;

        if (isErrorResponse(res)) {
          throw new Error(
            getResponseMessage(
              res,
              "Δεν φορτώθηκαν τα είδη πληρωμής."
            )
          );
        }

        setTransTypeOptions(getTransTypeOptions(res));
      } catch (error) {
        if (!isActive) return;

        setTransTypeOptions([]);
        setMetadataError(
          error?.message ?? "Δεν φορτώθηκαν τα στοιχεία της φόρμας πληρωμής."
        );
      } finally {
        if (isActive) {
          setIsMetadataLoading(false);
          setHasLoadedTransTypes(true);
        }
      }
    };

    fetchTransTypes();

    return () => {
      isActive = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    if (hideAttachments) {
      filesControl.initialize([]);
      filesControl.setIsDirty?.(false);
      setIsAttachmentsLoading(false);
      return undefined;
    }

    let isActive = true;

    const fetchAttachments = async () => {
      setIsAttachmentsLoading(true);

      try {
        const res = await getFiles(getAttachmentRequest(paymentTransHeaderId));
        if (!isActive) return;

        filesControl.initialize(formatFiles(res?.data ?? []));
        filesControl.setIsDirty?.(false);
      } catch (error) {
        if (!isActive) return;

        filesControl.initialize([]);
        filesControl.setIsDirty?.(false);
        enqueueSnackbar("Δεν φορτώθηκαν τα συνημμένα πληρωμής.", {
          variant: "warning",
        });
      } finally {
        if (isActive) {
          setIsAttachmentsLoading(false);
        }
      }
    };

    fetchAttachments();

    return () => {
      isActive = false;
    };
  }, [hideAttachments, open, paymentTransHeaderId]);

  useEffect(() => {
    if (!open || !isSupplierVisible) {
      setSupplierOptions([]);
      setSearchedSupplierText("");
      setHasSupplierSearchError(false);
      setIsSupplierLookupLoading(false);
      return undefined;
    }

    const currentSearch = normalizeSupplierSearch(supplierSearch);

    if (!currentSearch) {
      setSupplierOptions([]);
      setSearchedSupplierText("");
      setHasSupplierSearchError(false);
      setIsSupplierLookupLoading(false);
      return undefined;
    }

    setSupplierOptions([]);
    setSearchedSupplierText("");
    setHasSupplierSearchError(false);
    setIsSupplierLookupLoading(true);

    let isActive = true;
    const timeoutId = setTimeout(async () => {
      try {
        const res = await searchSuppliers({
          search: currentSearch,
          pageNumber: 1,
          pageSize: 10,
        });
        if (!isActive) return;

        if (isErrorResponse(res)) {
          throw new Error(
            getResponseMessage(
              res,
              "Δεν ολοκληρώθηκε η αναζήτηση προμηθευτή."
            )
          );
        }

        const payload = getResponsePayload(res);
        setSupplierOptions(Array.isArray(payload) ? payload : []);
        setSearchedSupplierText(currentSearch);
      } catch (error) {
        if (!isActive) return;

        setSupplierOptions([]);
        setSearchedSupplierText("");
        setHasSupplierSearchError(true);
        enqueueSnackbar(
          error?.message ??
          "Προέκυψε σφάλμα κατά την αναζήτηση προμηθευτή.",
          { variant: "warning" }
        );
      } finally {
        if (isActive) {
          setIsSupplierLookupLoading(false);
        }
      }
    }, 350);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [isSupplierVisible, open, supplierSearch]);

  const clearSupplierValues = ({ keepAfm = "" } = {}) => {
    const clearedValues = {
      supplierId: null,
      supplierAfm: keepAfm,
      supplierSelectedAfm: "",
      supplierName: "",
      supplierCanEdit: false,
      supplierCommercialTitle: "",
      supplierDoy: "",
      supplierDoyDescr: "",
      street: "",
      streetNumber: "",
      postalCode: "",
      city: "",
    };

    Object.entries(clearedValues).forEach(([fieldName, value]) => {
      setValue(fieldName, value, { shouldDirty: true });
    });
    clearErrors(["supplierAfm", "supplierId"]);
  };

  useEffect(() => {
    if (!selectedTransType) return;

    setValue("paymentTypeName", selectedTransType.label ?? "", {
      shouldDirty: false,
    });
    setValue("paymentTypeSupplierMode", selectedTransType.supplierMode ?? "", {
      shouldDirty: false,
      shouldValidate: true,
    });

    if (isHiddenMode(selectedTransType.supplierMode)) {
      clearSupplierValues();
    }
  }, [selectedTransType, setValue]);

  const applySupplier = (supplier) => {
    const nextSupplierValues = mapSupplierToFormValues(supplier);

    Object.entries(nextSupplierValues).forEach(([fieldName, value]) => {
      setValue(fieldName, value, {
        shouldDirty: true,
        shouldValidate: fieldName === "supplierId",
      });
    });
    clearErrors(["supplierAfm", "supplierId"]);
  };

  const openCreateSupplierDialog = (searchText = "") => {
    const possibleAfm = normalizeAfm(searchText);

    clearSupplierValues();
    setSupplierDialogState({
      open: true,
      supplier: null,
      initialAfm: /^\d{9}$/.test(possibleAfm) ? possibleAfm : "",
    });
  };

  const handleSupplierInputChange = (_, value, reason) => {
    if (reason === "input") {
      setSupplierSearch(value);
      if (supplierId) {
        clearSupplierValues();
      }
      return;
    }

    if (reason === "clear") {
      setSupplierSearch("");
      clearSupplierValues();
    }
  };

  const handleSupplierChange = (_, option) => {
    if (isCreateSupplierOption(option)) {
      openCreateSupplierDialog(option.searchText);
      setSupplierSearch("");
      return;
    }

    if (!option) {
      setSupplierSearch("");
      clearSupplierValues();
      return;
    }

    applySupplier(option);
    setSupplierSearch("");
    setSupplierOptions((prevOptions) => {
      const exists = prevOptions.some(
        (supplierOption) =>
          supplierOption.id?.toString() === option.id?.toString()
      );

      return exists ? prevOptions : [option, ...prevOptions];
    });
  };

  const handleEditSupplier = () => {
    const values = getValues();

    setSupplierDialogState({
      open: true,
      initialAfm: values.supplierAfm,
      supplier: {
        id: values.supplierId,
        afm: values.supplierAfm,
        onomasia: values.supplierName,
        commerTitle: values.supplierCommercialTitle,
        doy: values.supplierDoy,
        doyDescr: values.supplierDoyDescr,
        postalAddress: values.street,
        postalAddressNo: values.streetNumber,
        postalAreaDescription: values.city,
        postalZipCode: values.postalCode,
      },
    });
  };

  const handleCancelSupplierDialog = () => {
    setSupplierDialogState({ open: false, supplier: null, initialAfm: "" });
  };

  const handleSaveSupplier = async (supplierPayload) => {
    try {
      setIsSupplierSaving(true);
      const isSupplierEditMode = !!supplierPayload.id;
      const res = isSupplierEditMode
        ? await updateSupplier(supplierPayload)
        : await createSupplier(supplierPayload);

      if (isErrorResponse(res)) {
        enqueueSnackbar(
          getResponseMessage(res, "Δεν αποθηκεύτηκε ο προμηθευτής."),
          { variant: "error" }
        );
        return;
      }

      const supplier = getResponsePayload(res);
      applySupplier(supplier);
      setSupplierDialogState({ open: false, supplier: null, initialAfm: "" });
      enqueueSnackbar("Ο προμηθευτής αποθηκεύτηκε.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error?.message ??
        "Προέκυψε σφάλμα κατά την αποθήκευση προμηθευτή.",
        { variant: "error" }
      );
    } finally {
      setIsSupplierSaving(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setIsUploadingFiles(true);

      if (!hideAttachments && filesControl.isDirty) {
        const uploadRes = await filesControl.uploadFileGroups();

        if (isErrorResponse(uploadRes)) {
          enqueueSnackbar(
            getResponseMessage(
              uploadRes,
              "Δεν αποθηκεύτηκαν τα συνημμένα πληρωμής."
            ),
            { variant: "error" }
          );
          return;
        }
        filesControl.setIsDirty?.(false);
      }

      const bundleIds = hideAttachments
        ? initialValues?.bundleIds ?? []
        : getBundleIds(fileGroups, initialValues?.bundleIds);
      const shouldClearSupplier = isHiddenMode(supplierMode);
      const payload = {
        ...(isEditMode ? { id: paymentTransHeaderId } : {}),
        expTransHeaderId,
        transTypeId: Number(values.paymentTypeId),
        paymentDate: normalizeDate(values.paymentDate),
        paymentNumber: values.paymentNumber?.toString?.().trim?.() ?? "",
        description: emptyToNull(values.description),
        supplierId: shouldClearSupplier ? null : values.supplierId ?? null,
        cashAmount: amountOrNull(values.cashAmount),
        cardAmount: amountOrNull(values.cardAmount),
        bankAmount: amountOrNull(values.bankAmount),
        comments: emptyToNull(values.comments),
        remarks: emptyToNull(values.remarks),
        bundleIds,
      };

      await onSave?.(payload);
    } finally {
      setIsUploadingFiles(false);
    }
  };

  return (
    <FormProvider {...form}>
      <GlobalStyles
        styles={{
          ".expense-payment-date-popper": {
            zIndex: "1500 !important",
          },
        }}
      />
      <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
        <DialogTitle variant="h4">
          {isEditMode ? "Επεξεργασία Πληρωμής" : "Προσθήκη Πληρωμής"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {!isDialogReady ? (
            <Alert severity="info">Φόρτωση στοιχείων πληρωμής...</Alert>
          ) : (
            <Stack spacing={1.5}>
              {metadataError && <Alert severity="error">{metadataError}</Alert>}
              {isMetadataLoading && (
                <Alert severity="info">Φόρτωση επιλογών πληρωμής...</Alert>
              )}

              <Box sx={paymentFormSurfaceSx}>
                <PaymentFormRow label="Είδος Πληρωμής">
                  <Dropdown
                    name="paymentTypeId"
                    label={null}
                    options={transTypeOptions}
                    valueName="value"
                    labelName="label"
                    autoSelectSingleOption
                    disabled={isMetadataLoading}
                    changeFn={() => clearErrors(["paymentTypeId"])}
                    sx={inputSx}
                  />
                </PaymentFormRow>

                <PaymentFormRow label="Ημ/νία Πληρωμής">
                  <DateInput
                    name="paymentDate"
                    label={null}
                    variant="standard"
                    isFullWidth
                    sx={inputSx}
                    blurFn={() => clearErrors("paymentDate")}
                    datePickerProps={{
                      popperPlacement: "bottom-end",
                      popperClassName: "expense-payment-date-popper",
                      ...(hasSelectedValue(paymentDate) ? {} : { selected: null }),
                      popperModifiers: [
                        {
                          name: "preventOverflow",
                          options: {
                            boundary: "viewport",
                            padding: 8,
                          },
                        },
                      ],
                    }}
                  />
                </PaymentFormRow>

                <PaymentFormRow label="Αριθμός">
                  <TextInput
                    name="paymentNumber"
                    label={null}
                    variant="standard"
                    sx={inputSx}
                  />
                </PaymentFormRow>

                {isSupplierVisible && (
                  <>
                    <PaymentFormRow
                      label={`Προμηθευτής${isSupplierRequired ? " *" : ""}`}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "minmax(0, 1fr)",
                            sm: canEditSupplier
                              ? "minmax(0, 1fr) auto"
                              : "minmax(0, 1fr)",
                          },
                          gap: { xs: 0.5, sm: 0.75 },
                          alignItems: "flex-start",
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Controller
                            name="supplierId"
                            control={control}
                            render={({ field }) => (
                              <Autocomplete
                                options={supplierAutocompleteOptions}
                                value={selectedSupplier}
                                onChange={(event, option) => {
                                  field.onChange(
                                    isCreateSupplierOption(option)
                                      ? null
                                      : option?.id ?? null
                                  );
                                  handleSupplierChange(event, option);
                                }}
                                onInputChange={handleSupplierInputChange}
                                onBlur={() => {
                                  field.onBlur();
                                  trigger("supplierId");
                                }}
                                loading={isSupplierLookupLoading}
                                filterOptions={(options) => options}
                                noOptionsText={
                                  normalizeSupplierSearch(supplierSearch)
                                    ? "Δεν βρέθηκαν προμηθευτές"
                                    : "Πληκτρολογήστε για αναζήτηση"
                                }
                                loadingText="Αναζήτηση..."
                                clearText="Καθαρισμός"
                                openText="Άνοιγμα"
                                closeText="Κλείσιμο"
                                isOptionEqualToValue={(option, value) =>
                                  option.id?.toString() ===
                                  value?.id?.toString()
                                }
                                getOptionLabel={getSupplierOptionLabel}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props}>
                                    {isCreateSupplierOption(option) ? (
                                      <Typography
                                        sx={{ fontSize: 13, fontWeight: 800 }}
                                      >
                                        {getSupplierOptionLabel(option)}
                                      </Typography>
                                    ) : (
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          sx={{ fontSize: 13, fontWeight: 700 }}
                                        >
                                          {option.onomasia ||
                                            option.commerTitle ||
                                            "Προμηθευτής"}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            color: "text.secondary",
                                            fontSize: 12,
                                          }}
                                        >
                                          {[option.afm, option.commerTitle]
                                            .filter(Boolean)
                                            .join(" - ")}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <CssTextField
                                    {...params}
                                    fullWidth
                                    variant="standard"
                                    error={!!errors.supplierId}
                                    helperText={errors.supplierId?.message}
                                    InputProps={{
                                      ...params.InputProps,
                                      disableUnderline: true,
                                      endAdornment: (
                                        <>
                                          {isSupplierLookupLoading && (
                                            <CircularProgress
                                              color="inherit"
                                              size={18}
                                            />
                                          )}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                    sx={inputSx}
                                  />
                                )}
                              />
                            )}
                          />
                        </Box>
                        {canEditSupplier && (
                          <Tooltip title="Επεξεργασία στοιχείων προμηθευτή">
                            <IconButton
                              aria-label="Επεξεργασία στοιχείων προμηθευτή"
                              size="small"
                              color="primary"
                              onClick={handleEditSupplier}
                              sx={{
                                alignSelf: "flex-start",
                                height: 28,
                                justifySelf: { xs: "flex-end", sm: "end" },
                                mt: 0.25,
                                opacity: 0.72,
                                width: 28,
                                "&:hover": {
                                  opacity: 1,
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </PaymentFormRow>

                    <PaymentFormRow label="Επωνυμία">
                      <TextInput
                        name="supplierName"
                        label={null}
                        variant="standard"
                        disabled
                        sx={inputSx}
                      />
                    </PaymentFormRow>

                    <PaymentFormRow label="Δ/νση" minHeight={78}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) 120px",
                          gap: 1,
                        }}
                      >
                        <TextInput
                          name="street"
                          label={null}
                          variant="standard"
                          placeholder="Οδός"
                          disabled
                          sx={inputSx}
                        />
                        <TextInput
                          name="streetNumber"
                          label={null}
                          variant="standard"
                          placeholder="Αριθμός"
                          disabled
                          sx={inputSx}
                        />
                        <TextInput
                          name="postalCode"
                          label={null}
                          variant="standard"
                          placeholder="ΤΚ"
                          disabled
                          sx={inputSx}
                        />
                        <TextInput
                          name="city"
                          label={null}
                          variant="standard"
                          placeholder="Πόλη"
                          disabled
                          sx={inputSx}
                        />
                      </Box>
                    </PaymentFormRow>
                  </>
                )}

                <PaymentFormRow label="Αιτιολογία">
                  <TextInput
                    name="description"
                    label={null}
                    variant="standard" multiline
                    minRows={2}
                    sx={inputSx}
                  />
                </PaymentFormRow>

              </Box>

              <Box sx={{ ...paymentAmountsGridSx, mt: 3 }}>
                <PaymentAmountCell label="ΜΕΤΡΗΤΑ">
                  <CurrencyInput
                    name="cashAmount"
                    label={null}
                    variant="standard"
                    inputAlign="right"
                    defaultFromProps={false}
                    blurFn={handleAmountBlur("cashAmount")}
                    sx={inputSx}
                    fixedPadding={6}
                  />
                </PaymentAmountCell>

                <PaymentAmountCell label="ΚΑΡΤΑ">
                  <CurrencyInput
                    name="cardAmount"
                    label={null}
                    variant="standard"
                    inputAlign="right"
                    defaultFromProps={false}
                    blurFn={handleAmountBlur("cardAmount")}
                    sx={inputSx}
                    fixedPadding={6}
                  />
                </PaymentAmountCell>

                <PaymentAmountCell label="ΤΡΑΠΕΖΙΚΗ ΚΑΤΑΘΕΣΗ">
                  <CurrencyInput
                    name="bankAmount"
                    label={null}
                    variant="standard"
                    inputAlign="right"
                    defaultFromProps={false}
                    blurFn={handleAmountBlur("bankAmount")}
                    sx={inputSx}
                    fixedPadding={6}
                  />
                </PaymentAmountCell>

                <PaymentAmountTotal total={paymentAmountTotal} />
              </Box>

              <Box sx={{ mt: 1 }}>
                <CustomSimpleAccordion
                  title={
                    <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
                      Σχόλια - Παρατηρήσεις
                    </Typography>
                  }
                  content={
                    <Box sx={paymentFormSurfaceSx}>
                      <PaymentFormRow label="Σχόλια">
                        <TextInput
                          name="comments"
                          label={null}
                          variant="standard"
                          sx={inputSx}
                        />
                      </PaymentFormRow>

                      <PaymentFormRow label="Παρατηρήσεις">
                        <TextInput
                          name="remarks"
                          label={null}
                          variant="standard"
                          sx={inputSx}
                        />
                      </PaymentFormRow>
                    </Box>
                  }
                />
              </Box>

              {!hideAttachments && (
                <Box sx={formSurfaceSx}>
                  {isAttachmentsLoading ? (
                    <Alert severity="info">Φόρτωση συνημμένων πληρωμής...</Alert>
                  ) : (
                    <PaymentAttachments
                      settings={attachmentSettings}
                      isSaving={isUploadingFiles || isSaving}
                    />
                  )}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" color="error" onClick={onCancel}>
            Άκυρο
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(handleSave)}
            disabled={!canSave}
          >
            Αποθήκευση
          </Button>
        </DialogActions>
      </Dialog>

      <SupplierDialog
        open={supplierDialogState.open}
        supplier={supplierDialogState.supplier}
        initialAfm={supplierDialogState.initialAfm || supplierAfm}
        isSaving={isSupplierSaving}
        onCancel={handleCancelSupplierDialog}
        onSave={handleSaveSupplier}
      />
    </FormProvider>
  );
};

const PaymentDialog = ({ initialValues, ...props }) => {
  const paymentTransHeaderId = initialValues?.paymentTransHeaderId ?? null;

  return (
    <FileProvider
      downloadFile={(fileId, type) => downloadFile(fileId, type)}
      uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
      onDeleteBundle={async (bundleId) =>
        await deleteBundle(
          bundleId,
          PAYMENT_ATTACHMENTS_PROJECT_TABLE_ID,
          null,
          paymentTransHeaderId,
          null
        )
      }
      onDeleteFile={async (fileId) => {
        await deleteFile(
          fileId,
          PAYMENT_ATTACHMENTS_PROJECT_TABLE_ID,
          null,
          paymentTransHeaderId,
          null
        );
      }}
    >
      <PaymentDialogContent initialValues={initialValues} {...props} />
    </FileProvider>
  );
};

export default PaymentDialog;
